/*
 * Module for the snippet edit page
 */

define([
  "marionette",
  "backbone",
  "configuration/properties",
  "messaging/messenger",
  "i18n/i18n",
  "./snippetView",
  "./snippetEditorModal/snippetEditorModalModule",
  "./snippetModel",
], function (
  Marionette,
  Backbone,
  Properties,
  Messenger,
  i18Next,
  SnippetView,
  SnippetEditorModalModule,
) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "snippet";
      this.region = options.region;
      this._setUpReqres();
      this._setUpVents();
    },
    save: function () {
      if (this.snippet.isReadOnly) {
        Messenger.saveAttempted(true);
      } else {
        if (this.layout) {
          this.snippet.set("languageId", this.layout.getLanguageId());
          this.snippet.set("touchpointId", this.layout.getTouchpointId());
        }
        this.snippet.saveModel();
      }
    },
    show: function (snippet) {
      snippet.onSynced(function () {
        if (snippet.isNew()) {
          document.title = i18Next.t("snippet.new") + " - " + Properties.applicationName;
        } else {
          document.title = snippet.get("name") + " - " + Properties.applicationName;
        }
      });
      this.snippet = snippet;
      snippet.languages = Messenger.reqres.request("kb:languages");
      snippet.touchpoints = Messenger.reqres.request("kb:touchpoints");
      snippet.isReadOnly = !Messenger.reqres.request("user:is:admin");
      this.layout = new SnippetView.snippetLayout({ model: snippet });

      this.layout.on("edit:snippet:response", this._openResponseEditor, this);

      this.layout.on("delete:snippet", this._deleteSnippet, this);

      if (snippet.isNew()) {
        this.layout.on("show", () => {
          this.layout.enableTextbox("#snippetNameText");
        });
      }

      this.snippet.on("invalid", (_model, error) => {
        if ("name" in error) {
          // show error in a dialog and re-enable textbox for user to fix the name field value
          Messenger.okDialog(error["name"]);
          this.layout.enableTextbox("#snippetNameText");
        } else if ("value" in error) {
          Messenger.okDialog(error["value"]);
        }
      });

      this.listenTo(snippet, "update:preview", () => {
        this.layout.render();
      });

      this.showRegion(this.layout, {
        loading: true,
        entities: [snippet, snippet.languages, snippet.touchpoints],
      });
    },
    _deleteSnippet: function () {
      if (this.snippet.isNew()) {
        Messenger.yesNoDialog(i18Next.t("snippet.dialog.confirmDelete"), {
          yes: () => {
            this.snippet.destroy(); // destroy should remove the model from collection
            Messenger.vent.trigger("cancel:textbox");
            // redirect to topics page
            Messenger.vent.trigger("show:topics");
          },
        });
      } else {
        Messenger.confirmDialog(
          i18Next.t("snippet.dialog.typeToConfirmDelete"),
          {
            confirm: async (comment) => {
              if (comment && comment.toUpperCase() === "DELETE") {
                await this.snippet.deleteModelPromise();
                // redirect to topics page
                // TODO: should we re-direct to snippets page, save selected folder and resume work?
                Messenger.vent.trigger("show:topics");
              }
            },
          },
          this,
          "snippet.remove",
          {
            doNotModifyPlaceholder: true,
            placeholder: "common.delete",
            textArea: false,
          },
        );
      }
    },
    _openResponseEditor: function () {
      const kb = Messenger.reqres.request("active:kb:model");
      Messenger.vent.trigger(
        "show:modal",
        SnippetEditorModalModule,
        {
          namespace: "snippet",
          allowAiFeatures: kb.get("aiFeatures"),
        },
        {
          languageId: this.layout.getLanguageId(),
          snippet: this.snippet,
          touchpointId: this.layout.getTouchpointId(),
        },
        {
          title: "snippet.responseEditor.title",
          closeButton: true,
          closeOnBackgroundClick: false,
          maximizeButton: true,
          modalWidth: "1000px",
          modalHeight: "800px",
          trackInteraction: {
            action: "Edit Snippet Response",
            id: this.snippet.id,
            idType: "Snippet",
            object: "Snippet",
          },
        },
      );
    },
    _setUpReqres: function () {
      Messenger.reqres.setHandler(
        "fetch:conversation:response:attributes",
        () => {
          if (!this.responseAttributes) {
            this.responseAttributes = new Backbone.Collection();
          }
          return this.responseAttributes;
        },
        this,
      );
      Messenger.reqres.setHandler(
        "fetch:conversation:response:categories",
        () => {
          if (!this.responseCategories) {
            this.responseCategories = new Backbone.Collection();
          }
          return this.responseCategories;
        },
        this,
      );
    },
    _setUpVents: function () {
      Messenger.vent.on(
        "update:snippet:value",
        (newValueString) => {
          this.snippet.set("value", newValueString);
          this.snippet.set("languageId", this.layout.getLanguageId());
          this.snippet.set("touchpointId", this.layout.getTouchpointId());
          if (!this.snippet.get("name")) {
            // update the preview
            this.layout.render();
            // enable the textbox so that user knows to fill in the name
            this.layout.enableTextbox("#snippetNameText");
            // if snippet does not yet have a name, then do not attempt to save the model.
            // User cannot exit the page until a valid name is given.
            return;
          }
          Messenger.vent.trigger("show:saving:display");
          this.snippet
            .saveModelPromise()
            .then(() => this.layout.render())
            .catch((errorMsg) => {
              console.error("Could not update snippet", errorMsg);
              Messenger.okDialog(errorMsg);
            })
            .finally(() => Messenger.vent.trigger("close:saving:display"));
        },
        this,
      );
    },
  });
});
